import axios from "axios";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { url } from "@/store/initialStatus";
import { Translate } from "@/helpers/Translate";
import { PrintInvoice } from "@/helpers/func";
import PayInstallment from "../../onlinepayment/PayInstallment";
import MDBDataTableV5 from "../../CustomDataTable";
import "./styles.css";

const t = new Translate();

// 1000.1999999 -> 1,000.20
const format_number = (number: any) => {
  return Number(((number ?? 0) - 0).toFixed(2)).toLocaleString();
};

// ---- Filters -------------------------------------------------------------

const all_option = { value: "all", label: t.translate("All") };
const servicenames_default = "all";
const students_default = "all";
const patment_status_default = "Overdue";
const patment_status_options = [
  all_option,
  { value: "paid", label: t.translate("Paid") },
  { value: "not yet", label: t.translate("Not Yet") },
  { value: "Overdue", label: t.translate("Overdue") },
];

// Shared react-select props: render the menu above modals / cards
const selectMenuProps = {
  styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999999 }) },
  menuPortalTarget: document.body,
  menuPosition: "fixed" as const,
};

/** Card modifier, icon and translated label for an installment status. */
const statusMeta = (status: string) => {
  const label =
    patment_status_options.find((o) => o.value === status)?.label ?? status;
  switch (status) {
    case "paid":
      return { modifier: "paid", icon: "check-circle", label };
    case "Overdue":
      return { modifier: "overdue", icon: "exclamation-circle", label };
    default:
      return { modifier: "pending", icon: "clock", label };
  }
};

// ---- Transactions table ----------------------------------------------------

const defaultTable = {
  columns: [
    { label: "#", field: "id" },
    { label: t.translate("Date"), field: "date" },
    { label: t.translate("Parent Account Number"), field: "account_no" },
    { label: t.translate("Amount"), field: "amount" },
    { label: t.translate("Parent Name"), field: "parentname" },
    { label: t.translate("Details"), field: "details" },
    { label: t.translate("Academic Year"), field: "year" },
    { label: t.translate("Notes"), field: "notes" },
    { label: t.translate("Print"), field: "print" },
  ],
  // Placeholder row while loading
  rows: [
    {
      id: <Spinner color="info" />,
      date: <Spinner color="info" />,
      account_no: <Spinner color="info" />,
      amount: <Spinner color="info" />,
      parentname: <Spinner color="info" />,
      details: <Spinner color="info" />,
      year: <Spinner color="info" />,
      notes: <Spinner color="info" />,
      displayname: <Spinner color="info" />,
      print: <></>,
    },
  ],
};

/**
 * School installments: the guardian's installments as cards (filterable by
 * student, service and status) with online payment, plus a table of past
 * transactions with printable invoices.
 */
const Schoolfees = () => {
  const [isModalOpen, ShowModal] = useState<boolean>(false);
  const guardian = useSelector((state: any) => state.Parent.guardian);

  const [hasLoaded, setHasLoaded] = useState(false);
  const [fees, setFees] = useState<Array<any>>([]);
  const [students, setStudents] = useState<Array<any>>([]);
  const [servicenames, setServicenames] = useState<Array<any>>([]);
  const [intDataTable, setIntDataTable] = useState(defaultTable);

  const [servicenames_filter, setServicenames_filter] =
    useState(servicenames_default);
  const [students_filter, setStudents_filter] = useState(students_default);
  const [patment_status_filter, setPatment_status_filter] = useState(
    patment_status_default,
  );

  const [ModalHeaderTitle, SetModalHeaderTitle] = useState<any>(null);
  // Toggled after a payment to reload the data
  const [feesUpdated, setFeesUpdated] = useState(false);
  // Payment gateway element; when set it replaces the page
  const [Pay, SetPayment] = useState<any>(null);

  const [Amount, SetAmount] = useState<any>("0");
  const [selectedInstallment, setSelectedInstallment] = useState<any>(null);

  // Load installments, students, service names and transactions.
  // Unpaid installments past their date are marked "Overdue".
  useEffect(() => {
    Swal?.showLoading();
    axios
      ?.post(`${url}schoolfees/getschoolfees.php`, {
        guardian_id: guardian?.guardian_id ?? "",
      })
      ?.then((res: any) => {
        const res_data_data = res?.data?.data;
        if (res_data_data) {
          if (res_data_data?.hasOwnProperty("accountinstallments")) {
            const accountinstallments =
              res_data_data["accountinstallments"] ?? [];
            if (accountinstallments) {
              const Date_now = Date.now();
              const accountinstallments_new = accountinstallments?.map(
                (accountinstallment: any) => {
                  const new_accountinstallment = { ...accountinstallment };
                  if ("not yet" === accountinstallment.status) {
                    const overdue =
                      Date_now > new Date(accountinstallment.date).getTime();
                    if (overdue) {
                      new_accountinstallment.status = "Overdue";
                    }
                  }
                  return new_accountinstallment;
                },
              );
              setFees(accountinstallments_new);
            }
          }
          if (res_data_data?.hasOwnProperty("students")) {
            setStudents(res_data_data["students"]);
          }
          if (res_data_data?.hasOwnProperty("credits")) {
            const newRows = res_data_data["credits"]?.map((credit: any) => {
              return {
                ...credit,
                print: (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      PrintInvoice(
                        credit?.account_no ?? "",
                        credit?.id ?? "",
                        "28" === (credit?.service_id ?? "") ||
                          "39" === (credit?.service_id ?? "") ||
                          "50" === (credit?.service_id ?? ""),
                      );
                    }}
                  >
                    <i className="fas fa-print" aria-hidden="true"></i>{" "}
                    {t.translate("Print")}
                  </button>
                ),
              };
            });
            setIntDataTable({ ...defaultTable, rows: newRows });
          }
          if (res_data_data?.hasOwnProperty("servicenames")) {
            setServicenames(
              res_data_data["servicenames"]?.map((servicename: any) => ({
                value: servicename.value,
                label: t.translate(servicename.label),
              })),
            );
          }
          setHasLoaded(true);
          Swal.close();
        } else {
          Swal.fire({
            icon: "error",
            title: t.translate("Try again later!"),
            showConfirmButton: false,
            showDenyButton: false,
            cancelButtonText: t.translate("Ok"),
            showCloseButton: false,
            showCancelButton: true,
          });
        }
      })
      ?.catch((error: any) => {
        console.log("error");
        console.log(error);
        Swal.fire({
          icon: "error",
          title: t.translate("Try again later!"),
          showConfirmButton: false,
          showDenyButton: false,
          cancelButtonText: t.translate("Ok"),
          showCloseButton: false,
          showCancelButton: true,
        });
      });
  }, [feesUpdated, guardian?.guardian_id]);

  // ---- Derived data (hooks must stay above the early returns) ------------

  const studentOptions = useMemo(() => [all_option, ...students], [students]);
  const serviceOptions = useMemo(
    () => [all_option, ...servicenames],
    [servicenames],
  );

  const selectedStudent = useMemo(
    () => studentOptions.find((x) => students_filter === x.value),
    [studentOptions, students_filter],
  );
  const selectedService = useMemo(
    () => serviceOptions.find((x) => servicenames_filter === x.value),
    [serviceOptions, servicenames_filter],
  );
  const selectedStatus = useMemo(
    () => patment_status_options.find((x) => patment_status_filter === x.value),
    [patment_status_filter],
  );

  const filteredFees = useMemo(
    () =>
      fees?.filter((fee: any) => {
        const students_flag =
          "all" === students_filter || students_filter === fee.student_id;
        const servicenames_flag =
          "all" === servicenames_filter ||
          servicenames_filter === fee.service_id;
        const patment_status_flag =
          "all" === patment_status_filter ||
          patment_status_filter === fee.status;
        return patment_status_flag && students_flag && servicenames_flag;
      }),
    [fees, students_filter, servicenames_filter, patment_status_filter],
  );

  // ---- Handlers ------------------------------------------------------------

  // react-select change handlers: ignore cleared values
  const onStudentChange = useCallback((e: any) => {
    if (e?.value) setStudents_filter(e.value);
  }, []);
  const onServiceChange = useCallback((e: any) => {
    if (e?.value) setServicenames_filter(e.value);
  }, []);
  const onStatusChange = useCallback((e: any) => {
    if (e?.value) setPatment_status_filter(e.value);
  }, []);

  /** Called by the payment gateway; reloads the data after a payment. */
  const paymentFinished = useCallback((installment: any) => {
    SetPayment(null);
    if (installment) {
      setFeesUpdated((prev) => !prev);
    }
  }, []);

  /** Opens the amount modal for an installment, prefilled with the rest. */
  const openPayModal = useCallback((fee: any) => {
    SetAmount(fee.amount);
    setSelectedInstallment(fee);
    ShowModal(true);
  }, []);

  const closePayModal = useCallback(() => ShowModal(false), []);

  /** Starts the gateway payment for the entered amount. */
  const startPayment = useCallback(() => {
    if (Amount > 0) {
      SetModalHeaderTitle(t.translate("Initiating the Payment..."));
      SetPayment(
        <PayInstallment
          amount={Amount}
          installment={selectedInstallment}
          code="EW"
          onFinish={paymentFinished}
        />,
      );
      ShowModal(false);
    }
  }, [Amount, selectedInstallment, paymentFinished]);

  if (!hasLoaded) {
    return (
      <div className="pp-fees-empty">
        <Spinner color="primary" />
        {t.translate("Loading")}
      </div>
    );
  }

  // Payment in progress: show the gateway full screen
  return (
    Pay ?? (
      <Fragment>
        {/* ---------------- Installments ---------------- */}
        <section className="pp-fees-section">
          <h3 className="pp-fees-section-title">
            <i className="fas fa-file-invoice-dollar" aria-hidden="true"></i>
            {t.translate("School installments")}
            <span className="pp-fees-count">{filteredFees?.length ?? 0}</span>
          </h3>

          {/* Filters */}
          <div className="pp-fees-filters">
            <div>
              <label className="pp-fees-filter-label">
                {t.translate("Student")}
              </label>
              <Select
                value={selectedStudent}
                options={studentOptions}
                onChange={onStudentChange}
                {...selectMenuProps}
              />
            </div>
            <div>
              <label className="pp-fees-filter-label">
                {t.translate("Service")}
              </label>
              <Select
                value={selectedService}
                options={serviceOptions}
                onChange={onServiceChange}
                {...selectMenuProps}
              />
            </div>
            <div>
              <label className="pp-fees-filter-label">
                {t.translate("Installment Status")}
              </label>
              <Select
                value={selectedStatus}
                options={patment_status_options}
                onChange={onStatusChange}
                {...selectMenuProps}
              />
            </div>
          </div>

          {/* Installment cards */}
          {filteredFees?.length ? (
            <div className="pp-fees-grid">
              {filteredFees.map((fee: any) => {
                const status = statusMeta(fee.status);
                const paid = (fee.paid ?? 0) - 0;
                const rest = (fee.amount ?? 0) - 0;
                const total = rest + paid;
                const paidPercent =
                  total > 0 ? Math.min(100, (paid / total) * 100) : 0;

                return (
                  <article
                    key={fee.id}
                    className={`pp-fee-card pp-fee-card--${status.modifier}`}
                  >
                    <div className="pp-fee-card-header">
                      <div>
                        <h4 className="pp-fee-service">
                          {t.translate(fee.servicename ?? "")}
                        </h4>
                        <span className="pp-fee-year">
                          {t.translate("Year")} :{" "}
                          {fee.academicyear ??
                            new Date(fee.date ?? "")?.getFullYear() ??
                            ""}
                        </span>
                      </div>
                      <span className="pp-fee-status">
                        <i
                          className={`fas fa-${status.icon}`}
                          aria-hidden="true"
                        ></i>
                        {status.label}
                      </span>
                    </div>

                    <div className="pp-fee-student">
                      <i
                        className="fas fa-user-graduate"
                        aria-hidden="true"
                      ></i>
                      <div>
                        <div className="pp-fee-student-name">
                          {fee.fullname ?? ""}
                        </div>
                        <span className="pp-fee-student-id">
                          {t.translate("Student ID")} : {fee.student_id ?? ""}
                        </span>
                      </div>
                    </div>

                    <div className="pp-fee-body">
                      <div className="pp-fee-due">
                        <i
                          className="far fa-calendar-alt"
                          aria-hidden="true"
                        ></i>
                        {t.translate("Date")} :{" "}
                        <strong>{fee.date ?? ""}</strong>
                      </div>

                      <div className="pp-fee-amounts">
                        <div>
                          <span className="pp-fee-amount-label">
                            {t.translate("Total Amount")}
                          </span>
                          <span className="pp-fee-amount-value">
                            {format_number(total)}
                          </span>
                        </div>
                        <div>
                          <span className="pp-fee-amount-label">
                            {t.translate("Paid")}
                          </span>
                          <span className="pp-fee-amount-value pp-fee-amount-value--paid">
                            {format_number(fee.paid ?? 0)}
                          </span>
                        </div>
                        <div>
                          <span className="pp-fee-amount-label">
                            {t.translate("Rest")}
                          </span>
                          <span className="pp-fee-amount-value pp-fee-amount-value--rest">
                            {format_number(fee.amount ?? 0)}
                          </span>
                        </div>
                      </div>

                      <div
                        className="pp-fee-progress"
                        role="progressbar"
                        aria-valuenow={Math.round(paidPercent)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="pp-fee-progress-bar"
                          style={{ width: `${paidPercent}%` }}
                        ></div>
                      </div>
                    </div>

                    {"paid" === fee?.status ? null : (
                      <div className="pp-fee-footer">
                        <button
                          type="button"
                          className="btn btn-primary pp-fee-pay"
                          onClick={() => openPayModal(fee)}
                        >
                          <i
                            className="fas fa-credit-card"
                            aria-hidden="true"
                          ></i>
                          {t.translate("Pay")}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="pp-fees-empty">
              <i className="fas fa-inbox" aria-hidden="true"></i>
              {t.translate("No installments found")}
            </div>
          )}
        </section>

        {/* ---------------- Transactions ---------------- */}
        <section className="pp-fees-section">
          <h3 className="pp-fees-section-title">
            <i className="fas fa-receipt" aria-hidden="true"></i>
            {t.translate("Transactions")}
          </h3>
          <MDBDataTableV5
            hover
            responsive
            borderless={false}
            bordered
            striped
            entriesOptions={[5, 20, 25]}
            entries={5}
            pagesAmount={4}
            data={intDataTable}
            fullPagination
            searchTop
            searchBottom={false}
          />
        </section>

        {/* ---------------- Pay amount modal ---------------- */}
        <Modal isOpen={isModalOpen} centered className="pp-fees-modal">
          <ModalHeader>{t.translate("Enter Amount")}</ModalHeader>
          <ModalBody>
            {selectedInstallment && (
              <div className="pp-fees-modal-summary">
                <strong>
                  {t.translate(selectedInstallment.servicename ?? "")}
                </strong>
                {selectedInstallment.fullname ?? ""} · {t.translate("Rest")} :{" "}
                {format_number(selectedInstallment.amount ?? 0)}
              </div>
            )}
            <label className="pp-fees-filter-label" htmlFor="pp-fees-amount">
              {t.translate("Please Enter the amount.")}
            </label>
            <div className="input-group">
              <input
                id="pp-fees-amount"
                className="form-control form-control-lg"
                value={Math.round(Amount)}
                type="number"
                min={1}
                onKeyDown={(e) => {
                  // No sign / exponent characters: keeps the amount positive
                  if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  // Ignore negative values (e.g. pasted "-500")
                  if (Number(e.target.value) < 0) return;
                  SetAmount(e.target.value);
                }}
              />
              <span className="input-group-text">{t.translate("EGP.")}</span>
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={closePayModal}
            >
              {t.translate("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!(Amount > 0)}
              onClick={startPayment}
            >
              {t.translate("Continue")}{" "}
              <i className="fas fa-arrow-right" aria-hidden="true"></i>
            </button>
          </ModalFooter>
        </Modal>
      </Fragment>
    )
  );
};

export default Schoolfees;
